import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Alert, IconButton } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';

const NotificationPrompt = ({ onRequestPermission, onDismiss }) => {
    const [show, setShow] = useState(false);
    const [permissionState, setPermissionState] = useState(Notification.permission);

    useEffect(() => {
        console.log('🔔 [NotificationPrompt] Checking permission state:', Notification.permission);
        setPermissionState(Notification.permission);

        if (Notification.permission === 'default') {
            // Show prompt after a short delay
            const timer = setTimeout(() => {
                console.log('🔔 [NotificationPrompt] Showing prompt (default)');
                setShow(true);
            }, 2000);
            return () => clearTimeout(timer);
        } else if (Notification.permission === 'denied') {
            // Show instructions if denied
            console.log('🔔 [NotificationPrompt] Showing prompt (denied)');
            setShow(true);
        }
    }, []);

    const handleRequest = async () => {
        if (permissionState === 'denied') {
            alert('Your notifications are blocked. Please click the lock icon in the address bar and set Notifications to "Allow", then refresh the page.');
        } else {
            await onRequestPermission();
            setShow(false);
        }
    };

    const handleDismiss = () => {
        setShow(false);
        if (onDismiss) onDismiss();
    };

    if (!show || permissionState === 'granted') {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 80,
                right: 20,
                zIndex: 9999,
                maxWidth: 400,
                animation: 'slideIn 0.3s ease-out',
                '@keyframes slideIn': {
                    from: {
                        transform: 'translateX(100%)',
                        opacity: 0,
                    },
                    to: {
                        transform: 'translateX(0)',
                        opacity: 1,
                    },
                },
            }}
        >
            <Alert
                severity={permissionState === 'denied' ? "warning" : "info"}
                icon={<NotificationsActiveIcon />}
                action={
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={handleDismiss}
                    >
                        <CloseIcon fontSize="inherit" />
                    </IconButton>
                }
                sx={{
                    boxShadow: 3,
                    '& .MuiAlert-message': {
                        width: '100%',
                    },
                }}
            >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {permissionState === 'denied' ? 'Notifications are Blocked' : 'Enable Notifications'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {permissionState === 'denied'
                        ? 'Please enable notifications in your browser settings to receive task updates.'
                        : 'Get instant updates when tasks are assigned to you'}
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleRequest}
                    sx={{ mt: 0.5 }}
                    color={permissionState === 'denied' ? "warning" : "primary"}
                >
                    {permissionState === 'denied' ? 'How to Enable' : 'Enable Notifications'}
                </Button>
            </Alert>
        </Box>
    );
};

export default NotificationPrompt;
